import L from 'leaflet'
import type { TravelLog, ItemKind, MediaItem } from './types'
import { state } from './state'
import { els } from './dom'
import { t } from './i18n'
import { cleanString, escapeHtml } from './utils'
import { deleteMedia, saveItems, saveMedia } from './storage'
import { inferMediaType, revokeCachedMedia } from './media'
import { findCountryAtLatLng } from './countries'
import { clearDraftMarker, map, renderDraftMarker } from './map'

export function closeCreateModal(): void {
  document.querySelector('.map-create-modal')?.remove()
  clearDraftMarker()
}

// ---- Create flow -----------------------------------------------------------

export function renderCreatePopup(
  latlng: L.LatLng,
  selectedRegion: string,
  onSaved: () => void,
): void {
  closeCreateModal()
  map.closePopup()
  renderDraftMarker(latlng)
  const country = selectedRegion || findCountryAtLatLng(latlng) || ''
  const { modal, form } = buildModal({
    titleText: t('addItem'),
    initial: {
      // kind: 'travel-log',
      title: '',
      note: '',
      visitedAt: new Date().toISOString().slice(0, 10),
    },
    titlePlaceholder: country || 'Lisbon',
    existingMedia: [],
  })
  form.addEventListener('submit', (event) => {
    void handleCreate(event as SubmitEvent, form, latlng, country, onSaved)
  })
  els.mapPanel.append(modal)
}

async function handleCreate(
  event: SubmitEvent,
  form: HTMLFormElement,
  latlng: L.LatLng,
  country: string,
  onSaved: () => void,
): Promise<void> {
  event.preventDefault()
  const data = new FormData(form)
  const kind = readKind(data)
  const media = await collectNewMedia(data, kind)

  if ((kind === 'photo' || kind === 'video') && media.length === 0) {
    els.mapStatus.textContent = t('urlOrFileRequired')
    return
  }

  const item: TravelLog = {
    id: crypto.randomUUID(),
    // kind,
    title: cleanString(data.get('title')),
    note: cleanString(data.get('note')),
    country,
    lat: latlng.lat,
    lng: latlng.lng,
    visitedAt: cleanString(data.get('visitedAt')),
    media,
    createdAt: new Date().toISOString(),
  }

  state.items.unshift(item)
  saveItems(state.items)
  state.selectedCountry = country
  closeCreateModal()
  map.closePopup()
  els.mapStatus.textContent = t('itemSaved')
  onSaved()
}

// ---- Edit flow -------------------------------------------------------------

export function renderEditPopup(item: TravelLog, onSaved: () => void): void {
  closeCreateModal()
  map.closePopup()
  const removed = new Set<string>()
  const { modal, form } = buildModal({
    titleText: t('editItem'),
    initial: {
      // kind: item.kind,
      title: item.title,
      note: item.note,
      visitedAt: item.visitedAt ?? '',
    },
    titlePlaceholder: item.country || 'Lisbon',
    existingMedia: item.media,
    onRemoveExisting: (mediaId) => removed.add(mediaId),
  })
  form.addEventListener('submit', (event) => {
    void handleEdit(event as SubmitEvent, form, item, removed, onSaved)
  })
  els.mapPanel.append(modal)
}

async function handleEdit(
  event: SubmitEvent,
  form: HTMLFormElement,
  original: TravelLog,
  removedExistingIds: Set<string>,
  onSaved: () => void,
): Promise<void> {
  event.preventDefault()
  const data = new FormData(form)
  const kind = readKind(data)
  const keptMedia = original.media.filter((m) => !removedExistingIds.has(m.id))
  const newMedia = await collectNewMedia(data, kind)
  const media = [...keptMedia, ...newMedia]

  if ((kind === 'photo' || kind === 'video') && media.length === 0) {
    els.mapStatus.textContent = t('urlOrFileRequired')
    return
  }

  // Garbage-collect blobs + cached object URLs for removed media
  const removedItems = original.media.filter((m) =>
    removedExistingIds.has(m.id),
  )
  await Promise.all(
    removedItems
      .filter((m) => m.source === 'indexeddb' && m.blobId)
      .map((m) => deleteMedia(m.blobId)),
  )
  revokeCachedMedia(removedItems.map((m) => m.blobId))

  const updated: TravelLog = {
    ...original,
    // kind,
    title: cleanString(data.get('title')),
    note: cleanString(data.get('note')),
    visitedAt: cleanString(data.get('visitedAt')),
    media,
  }

  const idx = state.items.findIndex((i) => i.id === original.id)
  if (idx >= 0) state.items[idx] = updated
  saveItems(state.items)
  closeCreateModal()
  els.mapStatus.textContent = t('itemSaved')
  onSaved()
}

// ---- Shared form builder ---------------------------------------------------

type ModalConfig = {
  titleText: string
  initial: {
    // kind: ItemKind
    title: string
    note: string
    visitedAt: string
  }
  titlePlaceholder: string
  existingMedia: MediaItem[]
  onRemoveExisting?: (mediaId: string) => void
}

function buildModal(config: ModalConfig): {
  modal: HTMLElement
  form: HTMLFormElement
} {
  const modal = document.createElement('div')
  const form = document.createElement('form')
  modal.className = 'map-create-modal'
  form.className = 'map-item-form'

  const dateValue =
    config.initial.visitedAt || new Date().toISOString().slice(0, 10)
  const kindOptions = (['travel-log', 
    // 'photo', 'video'
  ] as const)
    .map(
      (k) =>
        `<option value="${k}"${
          // k === config.initial.kind ? 
          ' selected'
          //  : ''
          }>${escapeHtml(t('travelLog'))}</option>`,
    )
    .join('')

  form.innerHTML = `
    <div class="map-item-form-title">${escapeHtml(config.titleText)}</div>
    <label>${escapeHtml(t('type'))}
      <select name="kind">${kindOptions}</select>
    </label>
    <label>${escapeHtml(t('title'))}
      <input name="title" required value="${escapeHtml(config.initial.title)}" placeholder="${escapeHtml(config.titlePlaceholder)}" />
    </label>
    <label>${escapeHtml(t('note'))}
      <textarea name="note" rows="2">${escapeHtml(config.initial.note)}</textarea>
    </label>
    <label>${escapeHtml(t('date'))}
      <input name="visitedAt" type="date" value="${escapeHtml(dateValue)}" />
    </label>
    ${config.existingMedia.length > 0 ? `<div class="existing-media" data-existing-media></div>` : ''}
    <div class="media-fields">
      <label>${escapeHtml(t('file'))}
        <input name="file" type="file" accept="image/*,video/*" multiple />
      </label>
      <label>${escapeHtml(t('mediaUrl'))}
        <textarea name="mediaUrl" rows="2" placeholder="https://instagram.com/p/...&#10;https://example.com/photo.jpg"></textarea>
      </label>
    </div>
    <div class="map-item-actions">
      <button type="submit" class="button primary">${escapeHtml(t('save'))}</button>
      <button type="button" class="button secondary" data-cancel>${escapeHtml(t('cancel'))}</button>
    </div>
  `
  modal.addEventListener('click', (event) => event.stopPropagation())
  form
    .querySelector('[data-cancel]')!
    .addEventListener('click', closeCreateModal)

  // Render existing-media chips with remove buttons
  if (config.existingMedia.length > 0) {
    const host = form.querySelector<HTMLElement>('[data-existing-media]')!
    config.existingMedia.forEach((media) => {
      const chip = document.createElement('span')
      chip.className = 'existing-media-chip'
      const label = document.createElement('span')
      label.className = 'existing-media-label'
      label.textContent =
        media.source === 'url' ? media.url : `${t('file')}`
      const remove = document.createElement('button')
      remove.type = 'button'
      remove.className = 'existing-media-remove'
      remove.textContent = '×'
      remove.setAttribute('aria-label', t('delete'))
      remove.addEventListener('click', () => {
        config.onRemoveExisting?.(media.id)
        chip.remove()
      })
      chip.append(label, remove)
      host.append(chip)
    })
  }

  modal.append(form)
  return { modal, form }
}

function readKind(data: FormData): ItemKind {
  const raw = data.get('kind')
  return raw === 'photo' || raw === 'video' ? raw : 'travel-log'
}

/** Build MediaItems from the file + URL form fields (used by both flows). */
async function collectNewMedia(
  data: FormData,
  kind: ItemKind,
): Promise<MediaItem[]> {
  const out: MediaItem[] = []

  // Files (any input[type=file][multiple])
  const files = data.getAll('file').filter((entry): entry is File => entry instanceof File && entry.size > 0)
  for (const file of files) {
    const blobId = crypto.randomUUID()
    await saveMedia(blobId, file)
    out.push({
      id: crypto.randomUUID(),
      source: 'indexeddb',
      type: file.type || (kind === 'video' ? 'video/mp4' : 'image/jpeg'),
      url: '',
      blobId,
    })
  }

  // URLs (one per line in the textarea)
  const urlBlob = cleanString(data.get('mediaUrl'))
  const urls = urlBlob
    .split(/[\r\n]+/)
    .map((line) => line.trim())
    .filter(Boolean)
  for (const url of urls) {
    out.push({
      id: crypto.randomUUID(),
      source: 'url',
      type: inferMediaType(url),
      url,
      blobId: '',
    })
  }

  return out
}
